import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, ExternalLink, Loader2, Heart, ChevronLeft, ChevronRight, Clock, Eye, MessageCircle, Tag, Share2, Sparkles, User, Calendar, Bookmark } from 'lucide-react';
import CommentsSection from './CommentsSection';
import AuthorPanel from './AuthorPanel';
import SuggestedArticlesSidebar from './SuggestedArticlesSidebar';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import axios from 'axios';

import { parseDate, getTimeAgo } from '../lib/dateUtils';
import { saveArticleLocally, isArticleSavedLocally } from './LibraryPage';
import { useSwipeToClose } from '../lib/useSwipeToClose';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to generate URL-friendly slug from title
const generateSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 100);
};

const ReaderPanel = ({ article, isOpen, onClose, onOpenAuth, onSelectArticle, zIndexBase = 1000, nested = false }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef(null);
  const hasOpenedRef = useRef(false);
  
  // Like state for internal articles
  const [likes, setLikes] = useState(0);
  const [userLiked, setUserLiked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);

  // Bookmark and author state
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const [authorData, setAuthorData] = useState(null);
  const [authorPanelOpen, setAuthorPanelOpen] = useState(false);
  
  // Related, recent and trending articles state
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [recentArticles, setRecentArticles] = useState([]);
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  // Mobile detection - matches Tailwind 'md' breakpoint (< 768px)
  const [isMobile, setIsMobile] = useState(() => 
    typeof window !== 'undefined' ? (window.innerWidth < 768 || window.matchMedia('(max-width: 767px)').matches) : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768 || window.matchMedia('(max-width: 767px)').matches);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Animation lifecycle state
  const [shouldRender, setShouldRender] = useState(Boolean(isOpen && article));
  const [isExiting, setIsExiting] = useState(false);
  const [exitConfig, setExitConfig] = useState(null);
  const [hasEntered, setHasEntered] = useState(false);
  const isClosingRef = useRef(false);
  const panelRef = useRef(null);
  const exitTimerRef = useRef(null);

  useEffect(() => {
    if (shouldRender) {
      const timer = setTimeout(() => {
        setHasEntered(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setHasEntered(false);
    }
  }, [shouldRender]);


  // Smooth dismiss handler: on mobile slides back out to the right before unmounting
  const handleDismiss = useCallback((options = {}) => {
    setAuthorPanelOpen(false);
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }

    const isMob = typeof window !== 'undefined' ? (window.innerWidth < 768 || window.matchMedia('(max-width: 767px)').matches) : false;
    if (isMob) {
      const fromSwipe = Boolean(options?.fromSwipe);
      const startOffset = options?.currentOffset || 0;
      const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 360;
      const remainingDist = Math.max(0, screenWidth - startOffset);
      const duration = fromSwipe && startOffset > 0
        ? Math.max(130, Math.min(240, Math.round((remainingDist / screenWidth) * 260)))
        : 240;

      setExitConfig({ duration, fromSwipe, startOffset });
      setIsExiting(true);

      exitTimerRef.current = setTimeout(() => {
        setShouldRender(false);
        setIsExiting(false);
        setExitConfig(null);
        isClosingRef.current = false;
        exitTimerRef.current = null;
        onClose();
      }, duration);
    } else {
      setShouldRender(false);
      setIsExiting(false);
      setExitConfig(null);
      isClosingRef.current = false;
      onClose();
    }
  }, [onClose]);

  // Swipe to close hook on mobile — disabled when an overlay panel (AuthorPanel) is active
  const { handlers: swipeHandlers, swipeOffset, isSwiping, isSnappingBack } = useSwipeToClose(handleDismiss, shouldRender && !authorPanelOpen, isMobile);

  // Sync open state with slide animation
  useEffect(() => {
    if (isOpen && article) {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
      isClosingRef.current = false;
      setIsExiting(false);
      setExitConfig(null);
      setShouldRender(true);
    } else if (!isOpen && shouldRender && !isClosingRef.current) {
      const isMob = typeof window !== 'undefined' ? (window.innerWidth < 768 || window.matchMedia('(max-width: 767px)').matches) : false;
      if (isMob) {
        setIsExiting(true);
        setExitConfig(null);
        exitTimerRef.current = setTimeout(() => {
          setShouldRender(false);
          setIsExiting(false);
          setExitConfig(null);
          exitTimerRef.current = null;
        }, 240);
        return () => {
          if (exitTimerRef.current) {
            clearTimeout(exitTimerRef.current);
            exitTimerRef.current = null;
          }
        };
      } else {
        setShouldRender(false);
        setIsExiting(false);
        setExitConfig(null);
      }
    }
  }, [isOpen, article?.id]);

  // Fetch related and recent articles
  useEffect(() => {
    const fetchDiscoveryArticles = async () => {
      if (!shouldRender || !article?.id) return;
      
      setLoadingRelated(true);
      try {
        const [relatedRes, recentRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/articles/related/${article.id}?limit=8`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/articles/recent?limit=8&exclude_id=${article.id}`).catch(() => ({ data: [] }))
        ]);
        
        setRelatedArticles(relatedRes.data || []);
        setRecentArticles(recentRes.data || []);

        axios.get(`${BACKEND_URL}/api/articles/trending?limit=10`)
          .then(res => setTrendingArticles(res.data || []))
          .catch(() => {
            axios.get(`${BACKEND_URL}/api/my-stories?limit=12`)
              .then(res => setTrendingArticles((res.data?.articles || []).filter(a => a.id !== article.id)))
              .catch(() => setTrendingArticles([]));
          });
      } catch (error) {
        console.error('Failed to fetch discovery articles:', error);
        setRelatedArticles([]);
        setRecentArticles([]);
      } finally {
        setLoadingRelated(false);
      }
    };
    
    fetchDiscoveryArticles();
  }, [shouldRender, article?.id]);

  // Reset state when article changes or panel opens
  useEffect(() => {
    setAuthorPanelOpen(false);
    if (article && shouldRender) {
      setLoading(true);
      setLikes(0);
      setUserLiked(false);
      setRelatedArticles([]);
      setRecentArticles([]);
      setCommentCount(article.comments_count || 0);
      hasOpenedRef.current = false;
      if (article.is_user_created || article.content) {
        fetchReactions();
        fetchCommentsCount();
      }
    }
  }, [article?.id, shouldRender]);

  // Comments count state
  const [commentCount, setCommentCount] = useState(article?.comments_count || 0);

  const fetchCommentsCount = async () => {
    const artId = article?.id || article?._id;
    if (!artId) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/comments/${artId}`);
      if (Array.isArray(response.data)) {
        setCommentCount(response.data.length);
      }
    } catch (error) {
      console.error('Failed to fetch comments count:', error);
    }
  };

  // Fetch reactions
  const fetchReactions = async () => {
    if (!article?.id) return;
    try {
      const token = localStorage.getItem('yegoNewsToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${BACKEND_URL}/api/articles/${article.id}/reactions`, { headers });
      setLikes(response.data.like_count || 0);
      setUserLiked(response.data.user_liked || false);
    } catch (error) {
      console.error('Failed to fetch reactions:', error);
    }
  };

  // Handle Like
  const handleLike = async () => {
    if (!isAuthenticated) {
      showToast('Please sign in to like articles', 'warning');
      return;
    }
    setLikeLoading(true);
    try {
      const token = localStorage.getItem('yegoNewsToken');
      const response = await axios.post(
        `${BACKEND_URL}/api/articles/${article.id}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setLikes(response.data.like_count);
      setUserLiked(response.data.liked);
      showToast(response.data.liked ? 'Liked!' : 'Like removed', 'like');
    } catch (error) {
      console.error('Like failed:', error);
      showToast('Failed to like', 'error');
    } finally {
      setLikeLoading(false);
    }
  };

  // Check bookmark status on article change
  useEffect(() => {
    if (!article || !shouldRender) return;

    const checkBookmarkStatus = () => {
      if (isArticleSavedLocally(article)) {
        setIsBookmarked(true);
        return;
      }

      const token = localStorage.getItem('yegoNewsToken');
      const articleId = article.id || article._id;
      if (token && articleId) {
        axios.get(`${BACKEND_URL}/api/auth/saved-articles`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then(res => {
          const savedIds = (res.data?.articles || res.data || []).map(a => String(a.id || a._id));
          setIsBookmarked(savedIds.includes(String(articleId)));
        })
        .catch(() => setIsBookmarked(false));
      } else {
        setIsBookmarked(false);
      }
    };

    checkBookmarkStatus();

    const handleSavedEvent = (e) => {
      const targetId = String(article.id || article._id || article.link || article.url || article.title || '');
      if (e.detail?.articleId && String(e.detail.articleId) === targetId) {
        setIsBookmarked(Boolean(e.detail.saved));
      } else {
        checkBookmarkStatus();
      }
    };

    window.addEventListener('articleSaved', handleSavedEvent);
    window.addEventListener('yego_saved_articles_changed', handleSavedEvent);
    return () => {
      window.removeEventListener('articleSaved', handleSavedEvent);
      window.removeEventListener('yego_saved_articles_changed', handleSavedEvent);
    };
  }, [article, shouldRender]);

  // Fetch author info if author_id, author, or source exists
  useEffect(() => {
    const authorKey = article?.author_id || article?.author || article?.source;
    if (!shouldRender || !authorKey) {
      setAuthorData(null);
      return;
    }
    axios.get(`${BACKEND_URL}/api/authors/${encodeURIComponent(authorKey)}`)
      .then(res => {
        if (res.data) setAuthorData(res.data);
      })
      .catch(err => {
        console.warn('Failed to fetch author data:', err);
        setAuthorData(null);
      });
  }, [article?.author_id, article?.author, article?.source, shouldRender]);

  // Handle Save / Bookmark
  const handleSaveArticle = async () => {
    if (!article) return;
    setBookmarkLoading(true);
    try {
      const savedNow = saveArticleLocally(article);
      setIsBookmarked(savedNow);

      const articleId = article.id || article._id;
      const token = localStorage.getItem('yegoNewsToken');
      if (token && articleId) {
        const endpoint = savedNow ? '/api/auth/save-article' : '/api/auth/unsave-article';
        await axios.post(`${BACKEND_URL}${endpoint}?article_id=${articleId}`, { article_id: articleId }, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(err => console.warn('Bookmark sync warning:', err));
      }
      showToast(savedNow ? 'Article saved to library' : 'Article removed from library', 'info');
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
      showToast('Failed to update bookmark', 'error');
    } finally {
      setBookmarkLoading(false);
    }
  };

  const handleAuthorClick = () => {
    if (nested) return; // Nested panels don't open another author panel
    setAuthorPanelOpen(true);
  };

  // For external articles, auto-open in new tab after timeout (iframe blocked)
  useEffect(() => {
    let timer;
    if (article && !article.is_user_created && shouldRender && loading) {
      timer = setTimeout(() => {
        if (!hasOpenedRef.current && article.link) {
          hasOpenedRef.current = true;
          window.open(article.link, '_blank', 'noopener,noreferrer');
          handleDismiss();
        }
      }, 2000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [article, shouldRender, loading, handleDismiss]);

  const handleIframeLoad = useCallback(() => {
    setLoading(false);
  }, []);

  if (!isMobile || !shouldRender || !article) return null;

  const isInternalArticle = Boolean(article.is_user_created || article.content);

  const openInNewTab = () => {
    if (article.link) {
      window.open(article.link, '_blank', 'noopener,noreferrer');
    }
  };

  const scrollToComments = () => {
    const el = document.getElementById('comments-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleShare = async () => {
    const slug = article ? (article.slug || generateSlug(article.title)) : '';
    const url = article?.link || (typeof window !== 'undefined' ? `${window.location.origin}/news/${slug}` : '');
    if (navigator.share) {
      try {
        await navigator.share({
          title: article?.title,
          url: url,
        });
      } catch (err) {
        // User cancelled share
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(url);
        showToast('Link copied to clipboard', 'info');
      } catch (err) {
        showToast('Failed to copy link', 'error');
      }
    }
  };

  // Dynamic mobile transform styling with hardware acceleration
  const getPanelStyle = () => {
    if (!isMobile) {
      return { transform: 'none' };
    }

    if (isSwiping) {
      return {
        transform: `translate3d(${Math.max(0, swipeOffset)}px, 0, 0)`,
        transition: 'none',
        willChange: 'transform',
        contain: 'paint',
      };
    }

    if (isSnappingBack) {
      return {
        transform: 'translate3d(0, 0, 0)',
        transition: 'transform 200ms cubic-bezier(0.25, 1, 0.5, 1)',
        willChange: 'transform',
        contain: 'paint',
      };
    }

    if (isExiting) {
      const exitDuration = exitConfig?.duration || 240;
      return {
        transform: 'translate3d(100%, 0, 0)',
        transition: `transform ${exitDuration}ms cubic-bezier(0.25, 1, 0.5, 1)`,
        willChange: 'transform',
        contain: 'paint',
      };
    }

    return {
      transform: 'translate3d(0, 0, 0)',
      contain: 'paint',
      willChange: 'transform',
    };
  };

  const getPanelAnimationClass = () => {
    if (!isMobile || isSwiping || isSnappingBack || isExiting || hasEntered) return '';
    return 'animate-mobile-panel-in';
  };

  // Dynamic backdrop opacity for mobile slide & touch drag
  const getBackdropStyle = () => {
    if (!isMobile) {
      return { opacity: 0.5 };
    }

    if (isSwiping) {
      const progress = Math.min(1, Math.max(0, swipeOffset / (window.innerWidth || 360)));
      return {
        opacity: Math.max(0, 0.5 * (1 - progress)),
        transition: 'none',
      };
    }

    if (isSnappingBack) {
      return {
        opacity: 0.5,
        transition: 'opacity 200ms ease-out',
      };
    }

    if (isExiting) {
      const exitDuration = exitConfig?.duration || 240;
      return {
        opacity: 0,
        transition: `opacity ${exitDuration}ms ease-out`,
        pointerEvents: 'none',
      };
    }

    return { opacity: 0.5 };
  };

  const getBackdropAnimationClass = () => {
    if (!isMobile || isSwiping || isSnappingBack || isExiting || hasEntered) return '';
    return 'animate-mobile-backdrop-in';
  };

  const authorAvatar = authorData?.profile_picture_url || authorData?.avatar || article?.author_image || article?.author_photo || (
    (user && (user.id === article?.author_id || user.name === article?.author || user.name === article?.source))
      ? (user.profile_picture_url || user.avatar)
      : null
  );

  const panelContent = (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/60 ${swipeOffset > 0 && isSwiping ? 'pointer-events-none' : ''} ${getBackdropAnimationClass()}`}
        style={{
          ...getBackdropStyle(),
          zIndex: zIndexBase - 1,
          touchAction: 'none',
          overscrollBehavior: 'contain',
        }}
        onClick={handleDismiss}
      />

      {/* Panel */}
      <div 
        ref={panelRef}
        key={article?.id}
        className={`fixed inset-y-0 right-0 w-full max-w-3xl lg:max-w-6xl xl:max-w-7xl reader-panel-theme bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 shadow-2xl flex flex-col overflow-hidden ${getPanelAnimationClass()}`}
        style={{
          ...getPanelStyle(),
          zIndex: zIndexBase,
          height: isMobile ? '100dvh' : '100%',
          maxHeight: isMobile ? '100dvh' : '100%',
          overscrollBehavior: 'contain',
          touchAction: 'pan-y',
        }}
        {...(isMobile ? swipeHandlers : {})}
      >
        
        {/* Header */}
        <div className="flex items-center justify-between px-3.5 py-3 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 flex-shrink-0 select-none">
          {/* Left side - Back button + Source info */}
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            {/* Back button for mobile */}
            <button
              onClick={handleDismiss}
              className="sm:hidden p-1.5 -ml-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full flex-shrink-0 text-gray-700 dark:text-gray-200"
              aria-label="Go back"
            >
              <ChevronLeft className="h-6 w-6 text-gray-800 dark:text-gray-200" />
            </button>
            <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm overflow-hidden">
              {authorAvatar ? (
                <img src={authorAvatar} alt="" className="w-full h-full object-cover" />
              ) : (
                article.source?.charAt(0) || 'Y'
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900 dark:text-white truncate leading-tight">{article.source || 'YEGO'}</p>
            </div>
          </div>
          {/* Right side - Action buttons */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {!isInternalArticle && article.link && (
              <button
                onClick={openInNewTab}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-700 dark:text-gray-200"
                title="Open in new tab"
                aria-label="Open in new tab"
              >
                <ExternalLink className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={handleDismiss}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-700 dark:text-gray-200"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div 
          key={article.id} 
          className="flex-1 overflow-auto reader-content bg-white dark:bg-gray-950"
          style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}
        >
          {isInternalArticle ? (
            /* Internal Article - Direct Content */
            <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto bg-white dark:bg-gray-950">
              <div className="lg:flex lg:gap-8 xl:gap-10 items-start">
                <div className="flex-1 min-w-0">
                  {/* Category Tag Pill at Top of Article */}
              {article.category && (
                <div className="mb-2.5">
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 text-[11px] font-semibold rounded-full border border-emerald-100 dark:border-emerald-800/60">
                    <Tag className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                    <span>{article.category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                  </span>
                </div>
              )}

              {/* Title - Modern, high-contrast, fully theme-adaptive */}
              <h1 className="text-xl sm:text-2xl md:text-[26px] font-bold text-gray-900 dark:text-white tracking-tight leading-snug mb-3 text-left font-sans">
                {article.title}
              </h1>

              {/* Byline with Icons */}
              <div className="flex flex-wrap items-center gap-3 text-[11.5px] text-gray-500 dark:text-gray-400 mb-3.5">
                <div className="flex items-center gap-1">
                  <User className="h-3.5 w-3.5 text-gray-400 dark:text-gray-400" />
                  <span className="font-medium text-gray-700 dark:text-gray-200">{article.author || article.source || 'YEGO Staff'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-gray-400 dark:text-gray-400" />
                  <span>{(() => {
                    const d = parseDate(article.published_at || article.created_at || article.date) || new Date();
                    return d.toLocaleDateString('en-US', {
                      month: 'short', 
                      day: 'numeric',
                      year: 'numeric'
                    });
                  })()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-400" />
                  <span>{Math.ceil(((article.content || article.description || '')).split(/\s+/).filter(Boolean).length / 200) || 1} min read</span>
                </div>
              </div>

              {/* Action Pills Bar */}
              <div className="flex items-center gap-2 mb-5 pb-1 overflow-x-auto no-scrollbar">
                <button
                  onClick={handleSaveArticle}
                  disabled={bookmarkLoading}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold transition-colors shadow-sm flex-shrink-0 ${
                    isBookmarked
                      ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                      : 'bg-white dark:bg-gray-800/90 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100'
                  }`}
                >
                  <Bookmark className={`h-3.5 w-3.5 ${isBookmarked ? 'fill-current text-emerald-600 dark:text-emerald-400' : 'text-gray-600 dark:text-gray-400'}`} />
                  <span>{isBookmarked ? 'Saved' : 'Save'}</span>
                </button>

                <button
                  onClick={handleShare}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/90 hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 transition-colors shadow-sm flex-shrink-0"
                >
                  <Share2 className="h-3.5 w-3.5 text-gray-600 dark:text-gray-400" />
                  <span>Share</span>
                </button>

                <button
                  onClick={scrollToComments}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/90 hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 transition-colors shadow-sm flex-shrink-0"
                >
                  <MessageCircle className="h-3.5 w-3.5 text-gray-600 dark:text-gray-400" />
                  <span>{commentCount}</span>
                </button>

                <button
                  onClick={handleLike}
                  disabled={likeLoading}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold transition-colors shadow-sm flex-shrink-0 ${
                    userLiked
                      ? 'bg-red-50 dark:bg-red-950/60 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800'
                      : 'bg-white dark:bg-gray-800/90 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100'
                  }`}
                >
                  <Heart className={`h-3.5 w-3.5 ${userLiked ? 'fill-current text-red-500' : 'text-gray-600 dark:text-gray-400'}`} />
                  <span>{likes}</span>
                </button>
              </div>

              {/* Hero Image - 9to5Google Floating Green Card Style */}
              {article.image_url && (
                <div className="mb-6">
                  <div className="relative my-4 mx-2.5 sm:mx-3 group/img">
                    {/* Top-Left Green Frame Accent */}
                    <div 
                      className="absolute -top-2.5 -left-2.5 w-1/4 h-[calc(100%+10px)] border-t-[2.5px] border-l-[2.5px] border-emerald-600 dark:border-emerald-500 pointer-events-none z-0" 
                      aria-hidden="true" 
                    />
                    {/* Bottom-Right Green Frame Accent */}
                    <div 
                      className="absolute -bottom-2.5 -right-2.5 w-[calc(100%+10px)] h-1/4 border-b-[2.5px] border-r-[2.5px] border-emerald-600 dark:border-emerald-500 pointer-events-none z-0" 
                      aria-hidden="true" 
                    />

                    {/* Foreground Image Card */}
                    <div className="relative z-10 w-full overflow-hidden bg-white dark:bg-gray-900 shadow-sm border border-gray-200/60 dark:border-gray-800">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-auto max-h-[500px] object-cover group-hover/img:scale-[1.01] transition-transform duration-300"
                        loading="eager"
                      />
                    </div>
                  </div>

                  {(article.photo_credit || article.caption) ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 px-2.5">
                      Photo Credit: {article.photo_credit || article.caption}
                    </p>
                  ) : article.source ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 px-2.5">
                      Photo: {article.source}
                    </p>
                  ) : null}
                </div>
              )}

              {/* Article Body Content */}
              <div className="text-gray-900 dark:text-gray-100">
                {/* Render content with inline images, videos, and formatting */}
                {(article.content || article.description || '').split(/(\[IMAGE:[^\]]+\]|\[VIDEO:[^\]]+\]|\[H2:[^\]]+\]|\[BOLD:[^\]]+\])/).map((part, idx) => {
                  if (!part) return null;

                  // Image - Standard clean image
                  const imageMatch = part.match(/\[IMAGE:([^\]]+)\]/);
                  if (imageMatch) {
                    return (
                      <div key={idx} className="my-5">
                        <img 
                          src={imageMatch[1]} 
                          alt="Article content" 
                          className="w-full h-auto max-h-[550px] object-contain bg-gray-50 dark:bg-gray-900 rounded-xl shadow-sm"
                          loading="lazy"
                        />
                      </div>
                    );
                  }
                  
                  // Video
                  const videoMatch = part.match(/\[VIDEO:([^\]]+)\]/);
                  if (videoMatch) {
                    const videoUrl = videoMatch[1];
                    const isEmbed = videoUrl.includes('youtube.com/embed') || videoUrl.includes('vimeo.com');
                    return (
                      <div key={idx} className="my-5">
                        {isEmbed ? (
                          <iframe
                            src={videoUrl}
                            className="w-full aspect-video rounded-xl shadow-sm"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <video
                            src={videoUrl}
                            controls
                            className="w-full max-h-[500px] rounded-xl bg-black"
                          />
                        )}
                      </div>
                    );
                  }
                  
                  // Heading
                  const headingMatch = part.match(/\[H2:([^\]]+)\]/);
                  if (headingMatch) {
                    return (
                      <h2 key={idx} className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mt-5 mb-2 tracking-tight">
                        {headingMatch[1]}
                      </h2>
                    );
                  }
                  
                  // Bold
                  const boldMatch = part.match(/\[BOLD:([^\]]+)\]/);
                  if (boldMatch) {
                    return <strong key={idx} className="font-bold text-gray-900 dark:text-white">{boldMatch[1]}</strong>;
                  }
                  
                  if (!part.trim()) return null;

                  // Split by newlines so consecutive paragraphs are properly separated without excessive whitespace
                  const paras = part.split(/\n+/).map(p => p.trim()).filter(Boolean);
                  return paras.map((paraText, pIdx) => (
                    <p key={`${idx}-${pIdx}`} className="text-gray-800 dark:text-gray-200 text-[14px] sm:text-[15px] leading-[1.65] mb-3 font-normal">
                      {paraText}
                    </p>
                  ));
                })}
              </div>

              {/* Borderless Editorial Author Signature */}
              <div 
                onClick={nested ? undefined : handleAuthorClick}
                className="pt-8 pb-4 border-t border-gray-150 dark:border-gray-800/80 flex items-start sm:items-center justify-between gap-4 cursor-pointer group select-none"
                title={nested ? undefined : "View all articles by this author"}
                role={nested ? undefined : "button"}
                tabIndex={nested ? undefined : 0}
              >
                <div className="flex items-center gap-3.5 sm:gap-4 min-w-0">
                  {/* Author Avatar with subtle status ring */}
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 sm:w-13 sm:h-13 rounded-full overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-bold flex items-center justify-center text-lg ring-2 ring-emerald-500/20 dark:ring-emerald-500/30 group-hover:ring-emerald-500 transition-all">
                      {authorAvatar ? (
                        <img 
                          src={authorAvatar} 
                          alt={authorData?.name || article.author || article.source || 'Author'} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <span>{(authorData?.name || article.author || article.source || 'Y').charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                  </div>

                  {/* Author Details */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/70 px-2 py-0.5 rounded-full">
                        {authorData?.role === 'admin' ? 'Editor' : 'Author'}
                      </span>
                      {authorData?.article_count ? (
                        <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                          • {authorData.article_count} {authorData.article_count === 1 ? 'story' : 'stories'}
                        </span>
                      ) : null}
                    </div>

                    <h4 className="text-base font-bold text-gray-900 dark:text-white truncate leading-snug group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                      {authorData?.name || article.author || article.source || 'YEGO Staff'}
                    </h4>

                    {(authorData?.bio || article.author_bio) && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 leading-relaxed">
                        {authorData?.bio || article.author_bio}
                      </p>
                    )}
                  </div>
                </div>

                {!nested && (
                  <div className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400 group-hover:underline transition-all">
                    <span className="hidden sm:inline">All stories</span>
                    <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                )}
              </div>

              {/* LIKES & COMMENTS SECTION */}
              <div id="comments-section" className="mt-8 pt-6 border-t border-gray-150 dark:border-gray-800/80">
                <CommentsSection 
                  articleId={article.id || article._id} 
                  onOpenAuth={onOpenAuth}
                  onCommentCountChange={setCommentCount}
                />
              </div>

              {/* Back to Author Button at bottom of page for Nested Reader Panel */}
              {nested && (
                <div className="mt-8 mb-10 flex justify-center">
                  <button
                    onClick={handleDismiss}
                    className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-semibold text-sm rounded-full shadow-md active:scale-95 transition-all"
                  >
                    <ChevronLeft className="h-5 w-5" />
                    <span>Back to Author's Articles</span>
                  </button>
                </div>
              )}

            </div>

            {/* Vertical Dividing Line between Main Article and Vertical Feed */}
            <div className="hidden lg:block w-px self-stretch bg-gray-200/90 dark:bg-gray-800 flex-shrink-0" />

            {/* Right Column: Suggested Articles Sidebar (YouTube Style) */}
            <div className="hidden lg:block w-80 xl:w-[380px] 2xl:w-[420px] flex-shrink-0">
              <SuggestedArticlesSidebar
                currentArticleId={article.id}
                relatedArticles={relatedArticles}
                recentArticles={recentArticles}
                trendingArticles={trendingArticles}
                onArticleClick={(selectedArticle) => {
                  const contentDiv = document.querySelector('.reader-content');
                  if (contentDiv) contentDiv.scrollTo({ top: 0, behavior: 'smooth' });
                  if (onSelectArticle) {
                    onSelectArticle(selectedArticle);
                  } else {
                    window.dispatchEvent(new CustomEvent('switchArticle', { detail: selectedArticle }));
                  }
                }}
              />
            </div>
          </div>
        </div>
          ) : (
            /* External Article - Show loading then auto-redirect */
            <div className="h-full flex flex-col items-center justify-center p-8 text-center min-h-[400px] bg-white dark:bg-gray-950">
              <div className="w-full max-w-md">
                <div className="animate-pulse space-y-4">
                  <div className="h-48 bg-gray-100 dark:bg-gray-800 rounded-xl"></div>
                  <div className="h-6 bg-gray-100 dark:bg-gray-800 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-100 dark:bg-gray-800 rounded w-1/2"></div>
                  <div className="space-y-2 mt-4">
                    <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded"></div>
                    <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded"></div>
                    <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-4/5"></div>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-3 mt-8">
                  <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-200 font-medium">Opening article...</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Author Slide-out Panel with swipe-to-close — only for top-level reader, not nested */}
      {!nested && (
        <AuthorPanel
          authorId={article.author_id || authorData?.id || article.author || article.source || 'Admin'}
          authorData={authorData}
          isOpen={authorPanelOpen}
          onClose={() => setAuthorPanelOpen(false)}
          zIndexBase={zIndexBase + 60}
        />
      )}
    </>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(panelContent, document.body);
  }
  return panelContent;
};

export default ReaderPanel;
